/**
 * Heal The Earth site header (sections/hte-site-header.liquid).
 *
 * - Dropdowns: a real <button aria-expanded> per menu. Click/Enter/Space toggles,
 *   Escape closes and returns focus, clicking elsewhere or tabbing out closes.
 *   Mouse hover is handled in CSS, so this only matters for touch and keyboard.
 * - Mobile panel: opens under the bar, locks page scroll, Escape or a link
 *   click closes it, focus moves in on open and back to the button on close.
 * - Sticky: "always" adds a shadow once scrolled; "reveal" also hides the bar
 *   while scrolling down and brings it back on the way up.
 * - Cart count: follows Horizon's cart:update event; falls back to /cart.js.
 *
 * Safe to load more than once (theme editor re-renders the section).
 */
(function () {
  'use strict';

  function initHeader(shell) {
    if (!shell || shell.dataset.hteReady) return;
    shell.dataset.hteReady = 'true';

    /* ── Dropdowns ── */
    var drops = Array.prototype.slice.call(shell.querySelectorAll('[data-hte-drop]'));

    function setOpen(drop, open) {
      drop.classList.toggle('is-open', open);
      var btn = drop.querySelector('.hte-hdr-drop-toggle');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    function closeAll(except) {
      drops.forEach(function (d) { if (d !== except) setOpen(d, false); });
    }

    drops.forEach(function (drop) {
      var btn = drop.querySelector('.hte-hdr-drop-toggle');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var open = !drop.classList.contains('is-open');
        closeAll(drop);
        setOpen(drop, open);
        if (open) {
          var first = drop.querySelector('.hte-hdr-drop-menu a');
          if (first && e.detail === 0) first.focus(); // keyboard activation
        }
      });
      drop.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drop.classList.contains('is-open')) {
          setOpen(drop, false);
          btn.focus();
        }
      });
      drop.addEventListener('focusout', function (e) {
        if (!drop.contains(e.relatedTarget)) setOpen(drop, false);
      });
    });

    document.addEventListener('click', function (e) {
      if (!shell.contains(e.target)) closeAll();
    });

    /* ── Mobile panel ── */
    var burger = shell.querySelector('[data-hte-burger]');
    var panel = burger && document.getElementById(burger.getAttribute('aria-controls'));

    function setMenu(open) {
      if (!panel) return;
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      burger.setAttribute('aria-label', open ? burger.dataset.labelClose : burger.dataset.labelOpen);
      panel.hidden = !open;
      panel.classList.toggle('is-open', open);
      document.documentElement.classList.toggle('hte-menu-open', open);
      if (open) {
        // Sit directly under the bar wherever it is (it may be partly scrolled).
        var bar = shell.querySelector('.hte-hdr');
        shell.style.setProperty('--hte-hdr-top', Math.max(0, bar.getBoundingClientRect().bottom) + 'px');
        shell.classList.remove('is-hidden');
        var first = panel.querySelector('a');
        if (first) first.focus({ preventScroll: true });
      }
    }

    if (burger && panel) {
      burger.addEventListener('click', function () {
        setMenu(burger.getAttribute('aria-expanded') !== 'true');
      });
      panel.addEventListener('click', function (e) {
        if (e.target.closest('a')) setMenu(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && burger.getAttribute('aria-expanded') === 'true') {
          setMenu(false);
          burger.focus();
        }
      });
      // Leaving the mobile layout with the panel open must not leave the page locked.
      var desktop = window.matchMedia('(min-width: 1024px)');
      var onChange = function () { if (desktop.matches) setMenu(false); };
      if (desktop.addEventListener) desktop.addEventListener('change', onChange);
    }

    /* ── Sticky behaviour ── */
    var mode = shell.dataset.sticky;
    if (mode === 'always' || mode === 'reveal') {
      var lastY = window.scrollY;
      var ticking = false;
      var update = function () {
        ticking = false;
        var y = window.scrollY;
        shell.classList.toggle('is-scrolled', y > 4);
        if (mode === 'reveal' && !document.documentElement.classList.contains('hte-menu-open')) {
          var down = y > lastY;
          if (Math.abs(y - lastY) > 6) shell.classList.toggle('is-hidden', down && y > 120);
        }
        lastY = y;
      };
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      // Revealing on keyboard focus keeps links reachable when the bar is hidden.
      shell.addEventListener('focusin', function () { shell.classList.remove('is-hidden'); });
      update();
    }
  }

  /* ── Cart count (bound once per page) ── */
  function setCount(count) {
    document.querySelectorAll('[data-hte-cart-count]').forEach(function (el) {
      var changed = String(el.textContent).trim() !== String(count);
      el.textContent = count;
      if (el.hasAttribute('data-empty')) {
        el.setAttribute('data-empty', count === 0 ? 'true' : 'false');
        el.setAttribute('aria-label', count + ' items');
        if (changed && count > 0) {
          el.classList.remove('is-bumped'); void el.offsetWidth; el.classList.add('is-bumped');
        }
      }
    });
  }
  function refreshCart() {
    fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) setCount(cart.item_count); })
      .catch(function () { /* keep the server-rendered count */ });
  }
  if (!window.__hteCartBound) {
    window.__hteCartBound = true;
    // Horizon dispatches 'cart:update' (assets/events.js) after every cart change.
    document.addEventListener('cart:update', function (e) {
      var d = e.detail || {};
      // Product-form adds report the quantity just added, not the cart total.
      if (d.data && d.data.source === 'product-form-component') { refreshCart(); return; }
      var count = d.data && typeof d.data.itemCount === 'number' ? d.data.itemCount
        : d.resource && typeof d.resource.item_count === 'number' ? d.resource.item_count : null;
      if (count === null) refreshCart(); else setCount(count);
    });
    // Restored from the back/forward cache: the count may be stale.
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCart(); });
  }

  function initAll() {
    document.querySelectorAll('[data-hte-header]').forEach(initHeader);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
